<template>
  <div class="full-height-page-wrapper">
    <div class="d-flex align-items-center">
      <PageHeader
        showBreadcrumbs
        :page-title="`Hours and payments report - ${currentProjectName}`"
        :customBreadcrumbs="breadcrumbs"
      >
        <template #buttons>
          <div class="ml-3 table-control d-flex align-items-center">
            <b-form-checkbox
              size="sm"
              switch
              v-model="includeLoggedLastMonth"
              class="switch"
              >Include logged hours from last month</b-form-checkbox>
          </div>
          <div class="ml-3 table-control d-flex align-items-center">
            <b-form-checkbox
              size="sm"
              switch
              v-model="includePaidLastMonth"
              class="switch"
              >Include paid hours from last month</b-form-checkbox>
          </div>
          <div class="ml-3 table-control d-flex align-items-center">
            <b-form-checkbox
              size="sm"
              switch
              v-model="showCommercialHours"
              class="switch"
              >Show commercial hours</b-form-checkbox>
          </div>
          <CloseButton
            v-if="showCloseButton"
            tooltip="Close"
            :onClick="closeButtonHandler"
          />
        </template>
      </PageHeader>
    </div>

    <!-- Project selector when no data -->
    <div v-if="!isDataLoading && getData.length === 0 && projectsList.length" class="d-flex justify-content-start bg-white p-4">
      <multiselect
        class="multiselect-projects"
        v-model="currentProject"
        :options="projectsList"
        :placeholder="'Select project'"
        :track-by="'name'"
        :label="'name'"
        :allow-empty="false"
        :showLabels="false"
        @select="$emit('changeProject', $event)"
      />
    </div>

    <!-- Empty state when no data -->
    <div v-if="!isDataLoading && getData.length === 0" class="full-height-content">
      <EmptyStateNoResults
        title="No hours and payments report yet"
        description="No hours or payment data available. Once recorded, the report will appear here."
        customIcon="hourglass_bottom"
        customIconClasses="material-symbols-outlined empty-state__icon"
        :withoutButton="true"
      />
    </div>

    <!-- Main content when data exists or loading -->
    <fullscreen
      v-else
      ref="fullscreen"
      background="#F3F5F7"
      class="table-fullscreen table__theme-default"
      @change="fullScreenChange"
    >
      <div
        class="table-container"
        :class="{ 'bg-white': !isDataLoading && getData.length }"
      >
        <div
          class="d-flex justify-content-between align-items-center bg-white"
          style="padding: 0 12px"
        >
          <div
            class="d-flex align-items-center mb-4"
            v-if="!isDataLoading && getData.length"
          >
            <div
              v-if="projectsList.length && !isDataLoading"
              class="custom-margin-right"
            >
              <label class="table-text form__label m-0 mb-1 custom-label"
                >Project:</label
              >
              <multiselect
                class="multiselect-projects"
                v-model="projectData"
                :options="projectsList"
                :placeholder="'Select project'"
                :track-by="'name'"
                :label="'name'"
                :allow-empty="false"
                :showLabels="false"
                @select="getProjectPage"
              />
            </div>
            <UsersStateListComponent
              class="custom-margin-right"
              :table-name="uiTableName"
              :members-list="getMembers"
              @changeActive="changeActive($event)"
            />
            <div>
              <label class="table-text form__label m-0 mb-1 custom-label"
                >Commission:</label
              >
              <multiselect
                style="width: 244px"
                v-model="commissionState"
                :options="commissionOptions"
                track-by="value"
                label="label"
                :showLabels="false"
                :allow-empty="false"
                @select="changeStateCommission"
              >
              </multiselect>
            </div>
          </div>
          <div
            v-if="!isDataLoading && getData.length"
            class="d-flex align-items-center"
          >
            <b-button
              variant="success"
              v-if="$can(permissions.CreateFinancesInvoices)"
              type="button"
              class="button"
              size="sm"
              @click.prevent="showCreateInvoices"
            >
              Create invoice
            </b-button>
            <b-button
              class="button btn-icon ml-2"
              variant="outline-info"
              @click="toggleFullscreen"
            >
              <span class="material-icons">{{
                isFullScreen ? "fullscreen_exit" : "fullscreen"
              }}</span>
            </b-button>
          </div>
        </div>

        <div ref="tableContain">
          <b-table-simple
            v-if="!isDataLoading && getData.length"
            class="table__theme-default slideout__table position-relative"
            id="my-table"
            :key="`table-${showCommercialHours}`"
            :fixed="false"
            hover
            stripped
            responsive
          >
            <colgroup>
              <col />
            </colgroup>
            <template v-for="(member, memberIdx) in getMembers">
              <colgroup
                class="group-border"
                :key="`colgroup-${member.id}-${memberIdx}`"
                v-if="userState[member.id]"
              >
                <col v-for="n in columnsCountForUser" :key="n" />
              </colgroup>
            </template>
            <colgroup>
              <col />
            </colgroup>
            <colgroup>
              <col />
            </colgroup>
            <colgroup>
              <col />
            </colgroup>

            <b-thead
              head-variant="light"
              :class="{ 'sticky-thead': getCoordinates > 0 }"
              :style="
                getCoordinates > 0
                  ? {
                      position: 'sticky',
                      top: getCoordinates + 'px',
                      'z-index': 10,
                      'background-color': '#f3f5f7',
                    }
                  : {}
              "
            >
              <b-tr>
                <b-th sticky-column rowspan="2">
                  <div
                    :ref="`thmonth`"
                    class="month-group "
                    style="padding-bottom: 0; padding-top: 6px"
                  >
                    Month
                  </div>
                </b-th>
                <template v-for="(member, i) in getMembers" variant="secondary">
                  <b-th
                    class="member-name"
                    :key="`member-th-${member.id}-${i}`"
                    variant="secondary"
                    :colspan="columnsCountForUser"
                    v-if="userState[member.id]"
                  >
                   <div class="member-group-header">
                      <div :ref="`userth${member.id}`">
                      <TableUserItem
                        class="text-center m-auto justify-content-center"
                        :user="member"
                        :item-max-width="'180px'"
                        view="name-only"
                      />
                    </div>
                      <div class="member-group-divider"></div>
                    </div>
                  </b-th>
                </template>
                <b-th
                  v-if="showCommission"
                  rowspan="2"
                  class="text-right"
                  style="min-width: 98px"
                  >Commission $</b-th
                >
                <b-th rowspan="2" class="text-right" style="min-width: 78px"
                  >Amount $</b-th
                >
                <b-th rowspan="2" class="text-right">Invoices</b-th>
              </b-tr>
              <b-tr
                :class="{ 'sticky-head-row3': getCoordinates > 0 }"
                :style="{ top: getCoordinates + 140 + 'px' }"
              >
                <template
                  v-for="(member, memberIdx) in getMembers"
                  variant="secondary"
                >
                  <b-th
                    class="text-right text-lowercase"
                    :key="`${member.id}-hl-header-${memberIdx}`"
                    v-if="userState[member.id]"
                    >Logged hours
                  </b-th>
                  <b-th
                    class="text-right text-lowercase"
                    :key="`${member.id}-hc-header-${memberIdx}`"
                    v-if="userState[member.id] && showCommercialHours"
                    >Commercial hours
                  </b-th>
                  <b-th
                    class="text-right text-lowercase"
                    :key="`${member.id}-hp-header-${memberIdx}`"
                    v-if="userState[member.id]"
                    >Paid hours
                  </b-th>
                </template>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr class="table-row" v-for="data in getData" :key="data.month">
                <b-td class="month-cell" sticky-column>{{
                  formatMonth(data.month)
                }}</b-td>
                <template v-for="(member, memberIndex) in data.members">
                  <b-td
                    class="text-right"
                    :key="`${member.id}-hl-${data.month}-${memberIndex}`"
                    v-if="userState[member.id]"
                    >{{ member.hours_logged || "-" }}</b-td
                  >
                  <b-td
                    class="text-right cursor-pointer"
                    :key="`${member.id}-hc-${data.month}-${memberIndex}`"
                    v-if="userState[member.id] && showCommercialHours"
                    :class="
                      member.hours_commercial != member.hours_logged &&
                      +member.hours_commercial !== 0
                        ? 'danger-commercial-hours'
                        : ''
                    "
                    @dblclick="
                      showChange(data.month, member.id, member.hours_commercial)
                    "
                  >
                    <span
                      class="commercial-hours-value"
                      v-if="!tdChange[`change-${data.month}-${member.id}`]"
                      >{{ member.hours_commercial || "0" }}</span
                    >
                    <div
                      v-else
                      class="d-flex justify-content-around align-items-center"
                    >
                      <span
                        class="material-icons cancel"
                        @click="hideChange(data.month, member.id)"
                        >close</span
                      >
                      <b-form-input
                        class="input-change"
                        type="number"
                        v-model="member.hours_commercial"
                        number
                        maxlength="5"
                        autofocus
                        @keyup.enter="
                          saveChange(
                            member.id,
                            data.month,
                            member.hours_commercial
                          )
                        "
                        @keyup.esc="hideChange(data.month, member.id)"
                      />
                      <span
                        class="material-icons done"
                        @click="
                          saveChange(
                            member.id,
                            data.month,
                            member.hours_commercial
                          )
                        "
                        >done</span
                      >
                    </div>
                  </b-td>
                  <b-td
                    class="text-right"
                    :key="`${member.id}-hp-${data.month}-${memberIndex}`"
                    v-if="userState[member.id]"
                  >
                    <div class="d-flex justify-content-end">
                      <div v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                           :class="getHoursClass(member)"
                           :title="
                             member.hours_paid +
                             ' paid hours, ' +
                             member.hours_not_paid +
                             ' not paid hours'
                           ">
                        {{ getTotalHours(member) }}
                      </div>
                    </div>
                  </b-td>
                </template>
                <b-td v-if="showCommission" class="text-right">
                  <div v-if="data.commission_not_paid">
                    {{
                      (data.commission + data.commission_not_paid) | round(2)
                    }}
                  </div>
                  <div v-else>{{ data.commission || "0" }}</div>
                </b-td>
                <b-td class="text-right">
                  <div class="d-flex justify-content-end">
                    <div v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
                         :class="getAmountClass(data)"
                         :title="
                           data.amount +
                           ' paid, ' +
                           data.amount_not_paid +
                           ' not paid'
                         ">
                      {{ getAmount(data) | round(2) }}
                    </div>
                  </div>
                </b-td>
                <b-td class="text-right" style="justify-items: center">
                  <b-button
                    v-if="data.invoices && data.invoices.length > 0"
                    :id="`invoices-${data.month}`"
                    @click.prevent="
                      onOpenInvoices(`popover-invoices-${data.month}`)
                    "
                    variant="link"
                    class="employee-wrap more invoices"
                    :style="{
                      width: '32px',
                      height: '32px',
                      backgroundColor: '#E4EFFF',
                      fontSize: '13px',
                      color: '#3D5170',
                    }"
                  >
                    {{ data.invoices.length }}
                  </b-button>
                  <span v-else>-</span>

                  <InvoicePopup
                    v-if="data.invoices && data.invoices.length > 0"
                    :title="`Invoices for ${formatMonth(data.month)}`"
                    :invoices="data.invoices"
                    :target="`invoices-${data.month}`"
                    :popover-ref="`popover-invoices-${data.month}`"
                    custom-class="table-sales-report-invoices-popover"
                    :show.sync="invoicePopovers[data.month]"
                    @close="onCloseInvoices(`popover-invoices-${data.month}`)"
                    @reloadInvoices="
                      loadHoursAndPaymentsPageData();
                      onCloseInvoices(`popover-invoices-${data.month}`);
                    "
                    @showEditForm="
                      (invoice) => showToTheTopAndEdit(invoice, data.month)
                    "
                  />
                </b-td>
              </b-tr>
            </b-tbody>
            <b-tfoot class="tfoot">
              <b-tr>
                <b-td sticky-column>Total</b-td>
                <template v-for="(totalData, i) in getTotalData">
                  <b-td
                    class="text-right"
                    :key="`${i}-hl`"
                    v-if="userState[totalData.id]"
                    >{{ totalData.total_hl || "0" | round(2) }}</b-td
                  >
                  <b-td
                    class="text-right"
                    :key="`${i}-hc`"
                    v-if="userState[totalData.id] && showCommercialHours"
                    >{{ totalData.total_hc || "0" | round(2) }}</b-td
                  >
                  <b-td
                    class="text-right"
                    :key="`${i}-hp`"
                    v-if="userState[totalData.id]"
                    >{{ totalData.total_hp || "0" | round(2) }}</b-td
                  >
                </template>
                <b-td v-if="showCommission"></b-td>
                <b-td class="text-right">{{ totalAmount | round(2) }}</b-td>
                <b-td></b-td>
              </b-tr>
              <b-tr>
                <b-td sticky-column>Prepaid h</b-td>
                <template v-for="(totalData, i) in getTotalData">
                  <b-td
                    class="text-right"
                    :key="i"
                    :colspan="columnsCountForUser"
                    v-if="userState[totalData.id]"
                    ><span
                      class="prepaid-badge"
                      :class="{
                        'prepaid-positive': totalData.prepaid > 0,
                        'prepaid-negative': totalData.prepaid <= 0,
                      }"
                      >{{ totalData.prepaid | round(2) }}</span
                    ></b-td
                  >
                </template>
              </b-tr>
              <b-tr>
                <b-td sticky-column></b-td>
                <b-td></b-td>
                <b-td></b-td>
                <b-td></b-td>
              </b-tr>
            </b-tfoot>
          </b-table-simple>
        </div>
        <div
          v-if="isDataLoading"
          class="content-loading-wrapper"
          style="min-height: 60vh"
        >
          <Preloader class="centered-preloader large-spinner" />
        </div>
      </div>
    </fullscreen>

    <FinanceInvoicesForm
      ref="invoiceModalCreate"
      v-model="invoiceFormData.show"
      :data="invoiceFormData.form"
      :title="invoiceFormData.title"
      :headerTitle="invoiceFormData.headerTitle"
      :submitButtonText="invoiceFormData.submitButtonText"
      :bankDetails="getOwnBankDetails"
      :templates="getTemplates"
      :projects="getActualProjects"
      :statuses="getStatuses"
      :types="getInvoicesTypes"
      :isLoading="getIsModalLoading"
      inModal
      @submit="saveFinanceInvoices"
      @invoiceFormClosed="onClose"
      :breadcrumbs="invoiceFormBreadcrumbs.length ? invoiceFormBreadcrumbs : [
        {
          text: 'Home',
          to: { name: 'home' },
        },
        {
          text: 'Finances reports hours and payments',
          to: { name: 'finances-reports-hours-and-payments' },
        },
        {
          text: currentProjectName,
          click: () => {
            invoiceFormData.show = false;
            $router.push({
              name: 'h-p-report',
              params: { id: currentProject.id },
            });
          },
        },
      ]"
    />
    <div v-if="!isDataLoading && getData.length">
      <div class="mt-20px">
        <b-card-group class="mb-3 gap-20">
          <div class="card-wrapper" style="flex-grow: 0.5">
            <h3 class="card-title">All prepaid hours</h3>
            <span
              class="prepaid-badge"
              :class="{
                'prepaid-positive': totalPrepaid > 0,
                'prepaid-negative': totalPrepaid <= 0,
              }"
              >{{ totalPrepaid | round(2) }}</span
            >
          </div>

          <div class="card-wrapper" style="flex-grow: 1">
            <h3 class="card-title">Comment</h3>
            <div
              v-if="$can(permissions.ReadAllComments)"
              class="position-relative pr-1"
            >
              <TextEditor
                class="text-left"
                show-text
                data-already-loaded
                empty-text="No comment"
                :is-wide-tooltip="true"
                :entity-type="commentEntityTypes.ReportsHoursAndPayments"
                :entity-id="currentProject.id"
              />
            </div>
            <span v-else>No comment</span>
          </div>
        </b-card-group>
      </div>
    </div>
  </div>
</template>

<script>
import Moment from 'moment'
import { cloneDeep } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { commentEntityTypes } from '@mocks/comment-entity-types'
import { permissions } from '@mocks/permissions'
import StickyHeaderMixin from '@/mixins/StickyHeaderMixin'
import TableUserItem from '@components/tables/TableUserItem'
import FinanceInvoicesForm from '@components/modals/FinanceInvoicesForm/FinanceInvoicesForm.vue'
import UsersStateListComponent from '@components/UsersStateListComponent'
import InvoiceFormModal from '../../mixins/InvoiceFormModal'
import InvoicePopup from '@components/InvoicePopup'
import TextEditor from '@components/TextEditor'
import PageHeader from '@components/PageHeader'
import Preloader from '@components/Preloader'
import ScrollPositionMixin from '@mixins/ScrollPositionMixin'
import EmptyStateNoResults from '@components/EmptyStateNoResults.vue'
import CloseButton from '@components/common/CloseButton.vue'

export default {
  name: 'TableWithDetails',
  mixins: [InvoiceFormModal, StickyHeaderMixin, ScrollPositionMixin],
  components: {
    TableUserItem,
    FinanceInvoicesForm,
    UsersStateListComponent,
    InvoicePopup,
    TextEditor,
    PageHeader,
    Preloader,
    EmptyStateNoResults,
    CloseButton
  },
  props: {
    currentProject: {
      type: Object,
      default: () => null
    },
    projectsList: {
      type: Array,
      default: () => []
    },
    breadcrumbs: {
      type: Array,
      default: () => []
    },
    invoiceFormBreadcrumbs: {
      type: Array,
      default: () => []
    },
    customOnClose: {
      type: Function,
      default: null
    },
    customSaveInvoice: {
      type: Function,
      default: null
    },
    showCloseButton: {
      type: Boolean,
      default: false
    },
    closeButtonHandler: {
      type: Function,
      default: null
    }
  },
  data () {
    return {
      permissions,
      isDataLoading: false,
      invoicesLoading: false,
      showCommission: false,
      includeLoggedLastMonth: false,
      includePaidLastMonth: false,
      showCommercialHours: false,
      showCommercialColumnKey: 'commercial_hours',
      rawData: [],
      userState: {},
      allActiveUsers: 0,
      quantityShowInvoices: 2,
      tdChange: {},
      tempValue: null,
      projectData: null,
      refreshCommercialHours: true,
      commissionOptions: [
        { label: 'Hide', value: false },
        { label: 'Show', value: true }
      ],
      commissionState: { label: 'Hide', value: false },
      invoicePopovers: {},
      isFullScreen: false
    }
  },
  mounted () {
    if (this.$can(permissions.ReadAllComments)) {
      this.fetchComments({ type: commentEntityTypes.ReportsHoursAndPayments })
    }
    this.loadHoursAndPaymentsPageData()
    this.loadTableHiddenColumns(this.uiTableName).then(() => {
      this.showCommercialHours = this.getTableHiddenColumnKeys(
        this.uiTableName
      ).includes(this.showCommercialColumnKey)
    })
    this.fetchInvoiceFormData()
    // Initialize commission state
    this.commissionState = this.showCommission
      ? { label: 'Show', value: true }
      : { label: 'Hide', value: false }
  },
  beforeDestroy () {
    // Clean up fullscreen scroll listener if it exists
    if (this.isFullScreen && this.$refs.fullscreen && this.$refs.fullscreen.$el) {
      const tableContainerElement = this.$refs.fullscreen.$el.querySelector('.table-container')
      if (tableContainerElement) {
        tableContainerElement.removeEventListener('scroll', this.handleFullscreenScroll)
      }
    }
  },
  watch: {
    currentProject: {
      deep: true,
      handler (v) {
        this.projectData = v
      }
    },
    projectsList: {
      deep: true,
      handler (v) {
        this.projectsList = v
      }
    },
    showCommercialHours (value) {
      if (value) {
        this.addHiddenColumnKey({
          tableName: this.uiTableName,
          key: this.showCommercialColumnKey
        })
      } else {
        this.removeHiddenColumnKey({
          tableName: this.uiTableName,
          key: this.showCommercialColumnKey
        })
      }
    }
  },
  computed: {
    ...mapGetters({
      getTableHiddenColumnKeys: 'frontView/getTableHiddenColumnKeys'
    }),
    currentProjectName () {
      return this.currentProject.name ?? this.currentProject.key
    },
    uiTableName () {
      return 'hours_and_payments_report_' + this.currentProject.key
    },
    columnsCountForUser () {
      return this.showCommercialHours ? 3 : 2
    },
    getMembers () {
      const data = cloneDeep(this.rawData)
      const members = Object.entries(data).map(([, monthData]) => {
        delete monthData.summary
        return monthData
      })
      let uniqueMembers = members.reduce(function (
        uniqueMembers,
        monthMembersData
      ) {
        for (const memberName in monthMembersData) {
          if (!uniqueMembers.hasOwnProperty(memberName)) {
            uniqueMembers[memberName] = monthMembersData[memberName]
          }
          uniqueMembers[memberName]['user']['jira_user_name'] = memberName
        }
        return uniqueMembers
      },
      [])

      uniqueMembers = Object.entries(uniqueMembers)
        .map(([, user]) => {
          return user.user
        })
        .sort((a, b) => (a.name === 'By project' || a.name > b.name ? 1 : -1))

      return uniqueMembers
    },
    getData () {
      const data = Object.entries(this.rawData).map(([monthYM, monthData]) => {
        const item = {
          month: monthYM,
          commission: monthData.summary.commission,
          commission_not_paid: monthData.summary.commission_not_paid,
          amount: monthData.summary.amount,
          amount_not_paid: monthData.summary.amount_not_paid,
          invoices: monthData.summary.invoices,
          members: []
        }
        const members = this.getMembers
        for (const i in members) {
          const memberName = members[i].name
          const memberJiraUserName = members[i].jira_user_name

          let hoursLogged = 0
          let hoursPaid = 0
          let hoursNotPaid = 0
          let hoursCommercial = 0

          if (monthData[memberJiraUserName]) {
            hoursLogged = monthData[memberJiraUserName].summary.loggedHours
            hoursPaid = monthData[memberJiraUserName].summary.paidHours
            hoursNotPaid = monthData[memberJiraUserName].summary.notPaidHours
            hoursCommercial =
              monthData[memberJiraUserName].summary.commercialHours
          }

          if (monthYM === Moment().format('YYYY-MM')) {
            if (!this.includeLoggedLastMonth) {
              hoursLogged = null
            }
            if (!this.includePaidLastMonth) {
              hoursPaid = null
              hoursNotPaid = null
            }
          }

          item.members.push({
            id: members[i].id,
            name: memberName,
            position: members[i].position,
            employment_type: members[i].employment_type,
            payment_type: members[i].payment_type,
            hours_logged: hoursLogged,
            hours_paid: hoursPaid,
            hours_commercial: hoursCommercial,
            hours_not_paid: hoursNotPaid
          })
        }
        return item
      })

      return data
    },
    getTotalData () {
      // TODO remove or fix next line. cause it does nothing
      // eslint-disable-next-line no-unused-expressions
      this.refreshCommercialHours
      const total = this.getData.reduce(function (total, monthItem) {
        for (const id in monthItem.members) {
          if (total[id] === undefined) {
            total[id] = {
              id: monthItem.members[id].id,
              total_hl: 0,
              total_hc: 0,
              total_hp: 0,
              prepaid: 0
            }
          }
          total[id].total_hl += monthItem.members[id].hours_logged
          total[id].total_hc += monthItem.members[id].hours_commercial
          total[id].total_hp +=
            monthItem.members[id].hours_paid +
            monthItem.members[id].hours_not_paid
          total[id].prepaid = total[id].total_hp - total[id].total_hl
        }

        return total
      }, [])
      return total
    },
    totalPrepaid () {
      return this.getSum(this.getTotalData, 'prepaid')
    },
    totalAmount () {
      return this.getSum(this.getData, 'amount')
    },
    commentEntityTypes () {
      return commentEntityTypes
    }
  },
  methods: {
    ...mapActions({
      fetchComments: 'comments/fetchComments',
      loadTableHiddenColumns: 'frontView/loadTableHiddenColumns',
      addHiddenColumnKey: 'frontView/addHiddenColumnKey',
      removeHiddenColumnKey: 'frontView/removeHiddenColumnKey'
    }),
    getSum (arr, key) {
      let sum = 0
      for (let i in arr) {
        sum += arr[i][key]
      }
      return sum
    },
    getAmount (data) {
      return data.amount + data.amount_not_paid
    },
    getAmountClass (data) {
      const totalAmount = data.amount + data.amount_not_paid
      const paidAmount = data.amount

      if (paidAmount < totalAmount) {
        return 'paid-amount-partial'
      } else if (paidAmount >= totalAmount) {
        return 'paid-amount-complete'
      }
      return ''
    },
    getHoursClass (member) {
      const totalHours = member.hours_paid + member.hours_not_paid
      const paidHours = member.hours_paid

      // If both are 0, no background color
      if (totalHours === 0) {
        return ''
      }

      if (paidHours < totalHours) {
        return 'paid-amount-partial'
      } else if (paidHours >= totalHours) {
        return 'paid-amount-complete'
      }
      return ''
    },
    getTotalHours (member) {
      const totalHours = member.hours_paid + member.hours_not_paid
      if (totalHours === 0) {
        return '-'
      }
      return Math.round(totalHours * 100) / 100
    },
    formatMonth (monthString) {
      return Moment(monthString, 'YYYY-MM').format('MMM YY')
    },
    onOpenInvoices (key) {
      const popoverRef = key
      const popover = this.$refs[popoverRef]
      if (popover && popover[0]) {
        popover[0].$emit('open')
      }
    },
    onCloseInvoices (refKey) {
      const popover = this.$refs[refKey]
      if (popover && popover[0]) {
        popover[0].$emit('close')
      }
    },
    showToTheTopAndEdit (invoice, month) {
      // Close the popover first
      this.onCloseInvoices(`popover-invoices-${month}`)
      // Scroll to top and show edit form
      this.scrollToTheTop()
      this.showEditInvoice(invoice)
    },
    showCreateInvoices () {
      if (this.currentProject.id) {
        this.invoiceFormData.show = true
        this.$refs['invoiceModalCreate'].resetForm()
        const formFields = cloneDeep(
          this.$refs['invoiceModalCreate'].form.fields
        )
        const invoiceData = {}
        Object.keys(formFields).forEach(function (key) {
          invoiceData[key] = formFields[key].value
        })
        invoiceData.project = cloneDeep(this.projectData)
        this.invoiceFormData.form = invoiceData
        this.$refs['invoiceModalCreate'].changeProject(
          cloneDeep(this.projectData)
        )
      } else {
        this.$snotify.error(
          'Please create project ' +
            this.currentProjectName +
            ' before creating invoice',
          'Error',
          this.$store.state.snotify
        )
      }
    },
    async saveFinanceInvoices (fields) {
      if (this.customSaveInvoice) {
        // Use custom save handler for nested context
        await this.customSaveInvoice(fields)
      } else {
        // Default behavior
        await this.saveInvoice(fields)
      }
      this.loadHoursAndPaymentsPageData()
    },
    loadHoursAndPaymentsPageData () {
      this.fetchHoursAndPaymentsReport().then(() => {
        this.invoicesLoading = true

        this.fetchInvoices().then(() => {
          this.invoicesLoading = false
        })
      })
    },
    fetchHoursAndPaymentsReport () {
      return new Promise((resolve, reject) => {
        this.isDataLoading = true
        let params = {
          projects: [this.currentProjectName]
        }
        return this.$api.finances
          .getReportsHoursAndPayments(params)
          .then((response) => {
            if (response.status === 200) {
              this.rawData = response.data
            }
            resolve(true)
          })
          .catch((error) => {
            this.errorNotifications(error)
            reject(error)
          })
          .finally(() => {
            this.isDataLoading = false
          })
      })
    },
    changeStateCommission (selectedOption) {
      this.showCommission = selectedOption.value
      this.commissionState = selectedOption
      this.getThWidth()
    },
    fetchInvoices () {
      const params = '?page=1&filter[project_id]=' + this.currentProject.id
      return this.$api.finances.getInvoices(params)
    },
    changeActive (disUsers) {
      const members = this.getMembers
      for (const i in members) {
        if (disUsers.some((id) => members[i].id === id)) {
          this.userState[members[i].id] = false
        } else {
          this.userState[members[i].id] = true
        }
      }
      this.allActiveUsers = Object.values(this.userState).filter(
        (item) => !!item
      ).length
      this.getThWidth()
      this.$forceUpdate()
    },
    showChange (month, memberId, val = 0) {
      this.tdChange = {}
      this.tdChange[`change-${month}-${memberId}`] = true
      this.tempValue = val

      this.$forceUpdate()
    },
    hideChange (month, memberId) {
      const curMonth = this.getData.find((data) => data.month === month)
      const curMember = curMonth.members.find(
        (member) => member.id === memberId
      )

      curMember.hours_commercial = this.tempValue

      this.tdChange = {}
      this.tempValue = null
      this.$forceUpdate()
    },
    saveChange (userId, dateYM, hours) {
      this.tdChange = {}
      this.refreshCommercialHours++

      this.axios
        .post(
          '/api/finances/commercial-hours/save-to-hp-report',
          {
            project_id: this.currentProject.id,
            user_id: userId,
            date_ym: dateYM,
            hours
          },
          {
            headers: {
              Authorization: 'Bearer' + localStorage.getItem('access_token')
            }
          }
        )
        .then(() => {
          this.$snotify.success(
            'Commercial hours successfully saved',
            'Success',
            this.$store.state.snotify
          )
          this.$forceUpdate()
        })
        .catch((error) => {
          this.errorNotifications(error)
        })
    },
    getProjectPage (data) {
      this.$router.push({ name: 'h-p-report', params: { id: data.id } })
    },
    onClose () {
      // If we have a custom close handler, use it instead
      if (this.customOnClose) {
        this.customOnClose()
        return
      }

      // Default behavior
      const route = this.$router.resolve({
        name: this.$route.name,
        params: this.$route.params
      })
      window.history.pushState({}, '', route.href)
      this.restoreScrollPosition()
    },
    toggleFullscreen () {
      this.$refs.fullscreen.toggle()
    },
    fullScreenChange (fullscreen) {
      this.isFullScreen = fullscreen

      // Update scroll listener for fullscreen mode
      if (fullscreen) {
        // Remove window scroll listener and add table container listener
        window.removeEventListener('scroll', this.handleScroll)
        this.$nextTick(() => {
          // Find the scrolling container - it's the .table-container with overflow: scroll
          const fullscreenElement = this.$refs.fullscreen.$el
          const tableContainerElement = fullscreenElement?.querySelector('.table-container')
          if (tableContainerElement) {
            tableContainerElement.addEventListener('scroll', this.handleFullscreenScroll)
          }
        })
      } else {
        // Remove fullscreen scroll listener and restore window listener
        const fullscreenElement = this.$refs.fullscreen.$el
        const tableContainerElement = fullscreenElement?.querySelector('.table-container')
        if (tableContainerElement) {
          tableContainerElement.removeEventListener('scroll', this.handleFullscreenScroll)
        }
        window.addEventListener('scroll', this.handleScroll)
      }
    },
    handleFullscreenScroll () {
      // In fullscreen mode, calculate position relative to the scrolling container (.table-container)
      const fullscreenElement = this.$refs.fullscreen.$el
      const tableContainerElement = fullscreenElement?.querySelector('.table-container')
      const tableElement = this.$refs.tableContain

      if (tableContainerElement && tableElement) {
        // Get the scrolling container's bounding rect (the actual viewport for scrolling)
        const containerRect = tableContainerElement.getBoundingClientRect()
        // Get table's bounding rect relative to browser viewport
        const tableRect = tableElement.getBoundingClientRect()

        // Calculate table position relative to scrolling container's top
        // This mimics how getBoundingClientRect().top works in normal mode
        this.startTableCoordinates = tableRect.top - containerRect.top
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .wrapper {
  display: flex;
}
::v-deep .close-button {
  padding: 0;
}
::v-deep .table__theme-default table tr th {
    font-size: 13px;
}
.table__theme-default table.b-table > thead > tr > th
 {
    padding: 4px 4px 7px;
    line-height: 14px;
}

.table-container {
  padding: 14px 15px 0px 15px;
  border-radius: 10px;

  span {
    display: inline-block;
  }

  .preloader {
    background-color: transparent;
  }

  .table-row {
    height: 30px;
    td {
      padding-top: 3px;
      padding-bottom: 3px;
    }
  }

  .table-text {
    font-size: 12px;
    color: $fontColor3;
    font-weight: 400;
  }

  .slideout__table {
    td, th {
      color: $fontColorBase !important;
    }
  }

  .month-cell {
    padding-left: 5px;
  }

  .tfoot td:first-child {
    padding-left: 5px;
  }
}
.slideout__table {
  &.table__theme-default {
    .table {
      background-color: #fff;
      thead {
        border-bottom: 1px solid #dee2e6;
      }
      tr {
        th {
          text-transform: none;
          background-color: $bgColorBase;
        }
      }
    }
  }
  .group-border {
    border-left: 1px solid #dee2e6;
    border-right: 1px solid #dee2e6;
  }
  .tfoot {
    font-weight: bold;
    border-top: 1px solid #e4e7ee;
  }
}
.member-name {
  max-width: 200px;
  min-width: 200px;
  padding-bottom: 0 !important;

  ::v-deep.user-name {
    max-width: 140px;
  }
  ::v-deep.user-position {
    max-width: 120px;
  }
}
.month-group {
  min-width: 70px;
  line-height: 20px;
}
.month-cell {
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
}
.prepaid-badge {
  display: inline-block;
  padding: 0 4px;
  border-radius: 3px;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;

  &.prepaid-positive {
    background-color: #c3e6cb;
  }

  &.prepaid-negative {
    background-color: $notifyErrBgColor;
  }
}
.commission {
  font-size: 16px;
  cursor: pointer;
  color: #3d5170;
  .text-on {
    color: #3fa52f;
  }
  .text-off {
    color: #dc3545;
  }
}
.thead-row {
  display: none;
  &.sticky-head {
    min-height: 70px;
    padding: 0;
    display: table-row;
    align-items: center;
    background-color: $bgColorBase;
    position: absolute;
    left: 0;
    right: 0;
    z-index: 9;

    .sticky-head__second-line {
      span {
        padding: 6px 5px 10px;

        &:first-child {
          padding: 6px 10px 10px 0;
        }
        &:last-child {
          padding: 6px 0px 10px 10px;
        }
      }
    }
  }
}
.invoice-badge {
  font-size: 14px;
  padding: 5px 12px;
  background-color: #deebff;
}
.not-paid {
  padding: 0 5px;
  border-radius: 4px;
  background-color: #ffe5cd;
}
// Base class for all amount indicators with common styling
.amount-indicator {
  padding: 0 5px;
  border-radius: 4px;
  line-height: 20px;
}

.paid-amount-partial {
  @extend .amount-indicator;
  background-color: #FFDFC5;
}
.paid-amount-complete {
  @extend .amount-indicator;
  background-color: #C3E6CB;
}
.commercial-hours-value {
  text-decoration-line: underline;
}
.table-container .slideout__table td.danger-commercial-hours {
  color: #DC3545 !important;
}
.invoice-more {
  height: 100%;
  color: #007bff;
  cursor: pointer;
}
.invoice-td {
  display: flex;
  align-items: center;
  .b-toast {
    position: absolute;
  }
}
.variable-td {
  min-width: 100px;
  .val {
    font-weight: bold;
    text-decoration: underline;
  }

  input.form-control {
    width: 40px;
    height: 25px;
    padding: 4px;
    min-height: 25px;
    text-align: center;
  }
}
input.form-control {
    padding: 4px 0;
    text-align: center;
  }
.material-icons {
  &.done {
    color: #3fa52f;
  }
  &.cancel {
    color: #dc3545;
  }
}
.multiselect-projects {
  width: 200px;
}
.editor-container {
  background-color: #fff;
  border-radius: 10px;
  padding: 15px;
}
.custom-margin-right {
  margin-right: 10px;
}
.card-wrapper {
  padding: 10px 20px;
  background-color: white;
  border-radius: 10px;
}
.card-title {
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 30px;
}

// Invoice styles from TableSalesReportItem
.employee-wrap {
  background-color: $bgDefaultAvatar;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $bgColorWhite;
  border: 2px solid #fff;
  position: relative;
  text-decoration: none;
  font-size: 10px;

  &.more {
    background-color: $bgColorBase;
    color: $buttonTxtColor2;

    &:focus {
      color: #0063f8 !important;
      box-shadow: unset;
      background-color: #c3dbff;
      border-color: #0063f8;
    }
  }

  &.invoices {
    border-radius: 5px;
  }
}

// Fullscreen scrolling styles
.table-fullscreen.fullscreen {
  padding: 10px;
  overflow: auto;
}

// Table container specific overflow for this component
.table__theme-default > div.table-container {
  overflow: scroll;

  // Custom scrollbar styling
  &::-webkit-scrollbar {
    height: 8px;
    background: transparent;
  }

  &::-webkit-scrollbar-track {
    background: $bgColor2;
    border-radius: 25px;
    margin: 0 18px;
  }

  &::-webkit-scrollbar-thumb {
    background: $inputBorderColor2;
    border-radius: 25px;

    &:hover {
      background: #B0B0C8;
    }
  }

  // Firefox scrollbar
  scrollbar-width: thin;
  scrollbar-color: $inputBorderColor2 $bgColor2;
}

.member-group-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  line-height: 20px;
}

.member-group-divider {
  width: 100%;
  height: 1px;
  background: $bgColor2;
}
</style>
